import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import { ShieldCheck, AlertTriangle, ArrowRight } from "lucide-react";

/**
 * EscrowTermsPage — full legal disclaimer for CARTAL's optional escrow
 * mechanism.  The whole point of this page is to make crystal clear, in
 * both Arabic and English, that CARTAL is a purely technical facilitator
 * and bears NO liability for the underlying vehicle, ownership status,
 * legal title, or any obligations between buyer and seller.
 */
export default function EscrowTermsPage() {
  const { lang } = useI18n();
  const isAr = lang === "ar";

  const SECTIONS = [
    {
      ar: {
        h: "١. طبيعة الخدمة",
        b: "ضمان كارتال خدمة احتجاز مالي تقنية اختيارية، يستخدمها البائع والمشتري برضاهما. كارتال ليست طرفاً في عقد البيع، ولا تمتلك أو تحوز المركبة في أي وقت، ولا تضمن جودتها أو ملكيتها أو حالتها أو مطابقتها لما هو معلن.",
      },
      en: {
        h: "1. Nature of the service",
        b: "CARTAL Escrow is an optional technical money-holding service used by buyer and seller voluntarily. CARTAL is not a party to the sale contract, never owns or possesses the vehicle, and does not guarantee its quality, ownership, condition, or accuracy of any listing.",
      },
    },
    {
      ar: {
        h: "٢. حدود مسؤولية كارتال",
        b: "لا تتحمل كارتال أي مسؤولية قانونية، مالية، أو جزائية ناتجة عن: حالة المركبة الفنية أو الميكانيكية، صحة الأوراق الثبوتية، وجود ديون أو مخالفات، نزاع حول الملكية، الأضرار قبل أو أثناء أو بعد التسليم، فشل الفحص الفني الدوري، أو أي التزام آخر بين الأطراف.",
      },
      en: {
        h: "2. Limits of CARTAL's liability",
        b: "CARTAL bears no legal, financial, or penal liability arising from: the vehicle's technical or mechanical condition, validity of any documents, existence of debts or fines, ownership disputes, damage before/during/after delivery, periodic-inspection failure, or any other obligation between the parties.",
      },
    },
    {
      ar: {
        h: "٣. آلية الإفراج عن المبالغ",
        b: "تُحجز الأموال لمدة لا تتجاوز ٧ أيام عمل من تاريخ تحويل المشتري للمبلغ. تُفرج الأموال للبائع تلقائياً بعد تأكيد المشتري الاستلام أو انقضاء فترة المراجعة دون اعتراض موثّق. لا تفرج كارتال المبلغ في حال وُجد نزاع موثّق إلا بعد محاولة وساطة.",
      },
      en: {
        h: "3. Release mechanism",
        b: "Funds are held for up to 7 business days from buyer transfer. Funds are released automatically once the buyer confirms receipt OR after the review window lapses without a documented objection. CARTAL will not release on documented dispute until mediation is attempted.",
      },
    },
    {
      ar: {
        h: "٤. الوساطة عند النزاع",
        b: "تقدّم كارتال محاولة وساطة ودّية بين الأطراف بناءً على الأدلة المرفقة فقط (صور، فيديو، شهادة فحص، محادثات داخل المنصة). قرار كارتال في الوساطة استرشادي ولا يلزم أي طرف قانونياً. أي حقوق قانونية تبقى محفوظة لكلا الطرفين أمام الجهات الرسمية في المملكة العربية السعودية.",
      },
      en: {
        h: "4. Dispute mediation",
        b: "CARTAL offers a goodwill mediation based solely on attached evidence (photos, video, inspection certificate, in-platform chats). CARTAL's mediation outcome is advisory and does not legally bind either party. All legal rights remain reserved to both parties before the official authorities of the Kingdom of Saudi Arabia.",
      },
    },
    {
      ar: {
        h: "٥. ما لا يغطيه ضمان كارتال",
        b: "العيوب الخفية التي لم يذكرها البائع، الأضرار الناتجة عن استخدام المشتري بعد الاستلام، نزاعات الفحص الدوري الإلزامي، أي تحويل خارج منصة كارتال (لا توجد حماية للتحويلات خارج النظام)، الضرائب أو الزكاة أو رسوم نقل الملكية، خدمات الشحن أو التأمين أو التمويل (وهي مسؤولية مزوّدي تلك الخدمات).",
      },
      en: {
        h: "5. What CARTAL Escrow does NOT cover",
        b: "Hidden defects not disclosed by the seller, damage from buyer use after delivery, periodic-inspection disputes, any transfer made outside CARTAL (no protection for off-platform payments), taxes/zakat/registration fees, transport/insurance/financing services (those are the responsibility of the respective providers).",
      },
    },
    {
      ar: {
        h: "٦. الخدمات الاختيارية من طرف ثالث",
        b: "خدمات النقل (الشحن)، الفحص الفني، التأمين، والتمويل المعروضة على منصّة كارتال هي خدمات اختيارية بحتة من مقدّمين مستقلّين. المستخدم يختارها بإرادته الحرّة، ويُبرم عقده مباشرة مع مقدّم الخدمة. **كارتال لا تُجبر أحداً على اختيار أي خدمة، ولا تتحمّل أي مسؤولية عن جودتها، تسليمها، تسعيرها، أو أي ضرر ناتج عنها.** المسؤولية القانونية والمالية والفنية تقع بالكامل على مقدّم الخدمة أمام العميل مباشرة.",
      },
      en: {
        h: "6. Optional third-party services",
        b: "Transport, inspection, insurance, and financing services listed on CARTAL are strictly optional services provided by independent third parties. The user chooses them freely and contracts directly with the service provider. **CARTAL does not compel anyone to select any service, and bears no responsibility for their quality, delivery, pricing, or any resulting damage.** Legal, financial, and technical liability rests entirely with the service provider directly to the customer.",
      },
    },
    {
      ar: {
        h: "٧. الأنظمة المعمول بها",
        b: "تخضع هذه الشروط لأنظمة المملكة العربية السعودية. تختص محاكم المملكة بأي نزاع لم يحل عبر الوساطة.",
      },
      en: {
        h: "7. Governing law",
        b: "These terms are governed by the laws of the Kingdom of Saudi Arabia. KSA courts have exclusive jurisdiction over any dispute not resolved through mediation.",
      },
    },
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-12" data-testid="escrow-terms-page">
      <Link to="/trust" className="text-sm text-[#6b6b6b] hover:text-[#0a0a0a]">
        ← {isAr ? "العودة لمركز الثقة" : "Back to Trust Center"}
      </Link>

      <div className="flex items-start gap-4 mt-4 mb-2">
        <span
          className="inline-flex items-center justify-center w-14 h-14 rounded-2xl shrink-0"
          style={{
            backgroundColor: "rgba(201,165,92,0.10)",
            border: "1px solid rgba(201,165,92,0.45)",
          }}
        >
          <ShieldCheck className="w-7 h-7 text-[#8a6f2e]" />
        </span>
        <div>
          <div className="overline overline-gold mb-1">
            {isAr ? "وثيقة قانونية" : "Legal document"}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-medium text-[#0a0a0a] leading-tight">
            {isAr ? "شروط ضمان كارتال" : "CARTAL Escrow Terms"}
          </h1>
        </div>
      </div>
      <p className="text-sm text-[#6b6b6b] mb-8 mt-2">
        {isAr ? "آخر تحديث: فبراير ٢٠٢٦" : "Last updated: February 2026"}
      </p>

      {/* High-priority disclaimer */}
      <div
        className="flex items-start gap-3 rounded-2xl p-5 mb-8"
        style={{
          backgroundColor: "rgba(220,38,38,0.05)",
          border: "1.5px solid rgba(220,38,38,0.35)",
        }}
        data-testid="escrow-key-disclaimer"
      >
        <AlertTriangle className="w-5 h-5 text-red-700 mt-0.5 shrink-0" />
        <div className="text-sm text-[#3a3a3a] leading-relaxed">
          <strong className="text-red-800">
            {isAr ? "تنبيه أساسي — دور كارتال محدود:" : "Key notice — CARTAL's role is limited:"}
          </strong>{" "}
          {isAr
            ? "كارتال وسيط تقني (Technology Facilitator) فقط. لا نبيع، لا نشتري، لا ننقل، لا نفحص، لا نؤمّن، ولا نموّل. آلية «ضمان كارتال» تسهيل تقني لحجز المبلغ عبر بوابة الدفع (Moyasar) لحين تأكيد المشتري باستلام السيارة. الالتزامات القانونية والمالية بين البائع والمشتري ومقدّمي الخدمات المستقلّين — وكارتال لا تتحمّل أي مسؤولية عن المركبة، حالتها، ملكيّتها، أو أي خدمة اختيارية يختارها المستخدم."
            : "CARTAL is a Technology Facilitator only. We do not sell, buy, transport, inspect, insure, or finance. The 'CARTAL Guarantee' mechanism is a technical facility to hold funds via the payment gateway (Moyasar) until the buyer confirms vehicle receipt. Legal and financial obligations sit between buyer, seller, and independent service providers — CARTAL bears no liability whatsoever for the vehicle, its condition, its title, or any optional service the user chooses."}
        </div>
      </div>

      <div className="space-y-7">
        {SECTIONS.map((s, i) => {
          const t = isAr ? s.ar : s.en;
          return (
            <section key={i} data-testid={`escrow-section-${i + 1}`}>
              <h2 className="font-display text-xl text-[#0a0a0a] mb-2 font-semibold">{t.h}</h2>
              <p className="text-sm text-[#3a3a3a] leading-relaxed">{t.b}</p>
            </section>
          );
        })}
      </div>

      <div className="mt-12 surface rounded-2xl p-6">
        <h3 className="font-display text-lg text-[#0a0a0a] mb-2">
          {isAr ? "للتواصل بشأن النزاعات" : "Contact for disputes"}
        </h3>
        <p className="text-sm text-[#3a3a3a] leading-relaxed">
          {isAr ? (
            <>افتح نزاعك عبر <Link to="/disputes" className="text-[#8a6f2e] underline font-semibold">مركز فض النزاعات</Link> داخل كارتال. لن نتعامل مع طلبات الوساطة الواردة عبر البريد أو وسائل التواصل خارج المنصة.</>
          ) : (
            <>Open your dispute via <Link to="/disputes" className="text-[#8a6f2e] underline font-semibold">CARTAL Disputes Center</Link>. Mediation requests sent via email or off-platform channels will not be processed.</>
          )}
        </p>
      </div>

      <div className="mt-10 text-center">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-[#8a6f2e] hover:text-[#5a4615] font-semibold"
        >
          {isAr ? "العودة للصفحة الرئيسية" : "Back to homepage"}
          <ArrowRight className="w-4 h-4 rtl:rotate-180" />
        </Link>
      </div>
    </div>
  );
}
